import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Send, Trash2, Power, Mail, CalendarDays } from "lucide-react";
import { PageHeader, SectionCard, EmptyState } from "../components/ui/kaizen";

const AUDIENCES = [
  { id: "active", label: "Membres actifs" },
  { id: "inactive", label: "Anciens membres" },
  { id: "essai", label: "En essai" },
  { id: "all", label: "Tous les contacts" },
];

export default function AnnoncesModule() {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Formulaire
  const [titre, setTitre] = useState("");
  const [corps, setCorps] = useState("");
  const [banner, setBanner] = useState(true);
  const [expiresAt, setExpiresAt] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [emailAudience, setEmailAudience] = useState("active");
  const [emailSubject, setEmailSubject] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/annonces");
      setAnnonces(data.annonces || []);
    } catch {
      toast.error("Impossible de charger les annonces");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const publish = async () => {
    if (!titre.trim() || !corps.trim()) {
      toast.error("Titre et message requis");
      return;
    }
    if (sendEmail && !window.confirm(`Envoyer aussi cette annonce par courriel à l'audience « ${AUDIENCES.find(a => a.id === emailAudience)?.label} » ?`)) {
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/annonces", {
        titre: titre.trim(),
        corps: corps.trim(),
        banner,
        expires_at: expiresAt || null,
        active: true,
        send_email: sendEmail,
        email_audience: emailAudience,
        email_subject: emailSubject.trim() || null,
      }, { timeout: 120000 });
      if (data.email) {
        toast.success(`✅ Annonce publiée · courriel envoyé à ${data.email.sent} membre(s)${data.email.failed ? ` (${data.email.failed} échec)` : ""}`);
      } else {
        toast.success("✅ Annonce publiée dans le portail membres");
      }
      setTitre(""); setCorps(""); setExpiresAt(""); setSendEmail(false); setEmailSubject("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la publication");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/annonces/${a.id}`, {
        titre: a.titre, corps: a.corps, banner: !!a.banner,
        active: !(a.active !== false), expires_at: a.expires_at || null,
      });
      load();
    } catch {
      toast.error("Impossible de modifier l'annonce");
    }
  };

  const remove = async (a) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await api.delete(`/annonces/${a.id}`);
      toast.success("Annonce supprimée");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="annonces-module">
      <PageHeader
        icon={Megaphone}
        title="Annonces"
        subtitle="Publie une annonce dans le portail des membres (bannière) et, en option, envoie-la par courriel."
      />

      <SectionCard title="Nouvelle annonce" className="mb-8">
        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">Titre</label>
            <input
              data-testid="annonce-titre-input"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Ex. Fermeture du dojo — 13 au 21 juillet"
              className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-[#D90429] outline-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">Message</label>
            <textarea
              data-testid="annonce-corps-input"
              value={corps}
              onChange={(e) => setCorps(e.target.value)}
              rows={4}
              placeholder="Le dojo sera fermé du 13 au 21 juillet inclusivement. Reprise des cours le 22 juillet. Bon été !"
              className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-[#D90429] outline-none resize-y"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid="annonce-banner-toggle">
              <input type="checkbox" checked={banner} onChange={(e) => setBanner(e.target.checked)} className="accent-[#D90429] w-4 h-4" />
              Afficher en <b>bannière</b> prioritaire (en tête du portail)
            </label>
            <div className="flex items-center gap-2 text-sm">
              <CalendarDays size={16} className="text-white/50" />
              <span className="text-white/50">Expire le</span>
              <input
                data-testid="annonce-expiry-input"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="bg-black/40 border border-white/15 px-2 py-1.5 text-white focus:border-[#D90429] outline-none"
              />
            </div>
          </div>

          <div className="border-t border-white/10 pt-4">
            <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid="annonce-send-email-toggle">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-[#D90429] w-4 h-4" />
              <Mail size={16} className="text-[#D90429]" />
              Envoyer aussi cette annonce par <b>courriel</b>
            </label>
            {sendEmail && (
              <div className="grid sm:grid-cols-2 gap-4 mt-3 pl-7">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">Audience</label>
                  <select
                    data-testid="annonce-email-audience"
                    value={emailAudience}
                    onChange={(e) => setEmailAudience(e.target.value)}
                    className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-[#D90429] outline-none"
                  >
                    {AUDIENCES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">Objet (optionnel)</label>
                  <input
                    data-testid="annonce-email-subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="(par défaut : le titre de l'annonce)"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-[#D90429] outline-none"
                  />
                </div>
              </div>
            )}
          </div>

          <button
            data-testid="annonce-publish-btn"
            onClick={publish}
            disabled={saving}
            className="bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-50 text-white font-medium px-6 py-3 flex items-center gap-2 transition-colors"
          >
            <Send size={18} /> {saving ? "Publication…" : "Publier l'annonce"}
          </button>
        </div>
      </SectionCard>

      <SectionCard title={`Annonces publiées (${annonces.length})`}>
        {loading ? (
          <p className="text-white/50 text-sm">Chargement…</p>
        ) : annonces.length === 0 ? (
          <EmptyState icon={Megaphone} title="Aucune annonce" description="Publie ta première annonce ci-dessus." />
        ) : (
          <div className="space-y-3" data-testid="annonces-list">
            {annonces.map((a) => (
              <div key={a.id} className="border border-white/10 bg-black/30 p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-white">{a.titre}</p>
                    {a.banner && <span className="text-[10px] uppercase tracking-wider bg-[#D90429]/20 text-[#D90429] px-2 py-0.5">Bannière</span>}
                    {a.is_active_now
                      ? <span className="text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-400 px-2 py-0.5">Active</span>
                      : <span className="text-[10px] uppercase tracking-wider bg-white/10 text-white/50 px-2 py-0.5">Inactive/expirée</span>}
                  </div>
                  <p className="text-sm text-white/60 mt-1">{a.corps}</p>
                  {a.expires_at && <p className="text-xs text-white/40 mt-1">Expire le {a.expires_at}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`annonce-toggle-${a.id}`} onClick={() => toggleActive(a)} title={a.active === false ? "Activer" : "Désactiver"}
                    className="border border-white/15 hover:border-white/40 p-2 transition-colors">
                    <Power size={16} className={a.active === false ? "text-white/40" : "text-emerald-400"} />
                  </button>
                  <button data-testid={`annonce-delete-${a.id}`} onClick={() => remove(a)} title="Supprimer"
                    className="border border-white/15 hover:border-[#D90429] p-2 transition-colors">
                    <Trash2 size={16} className="text-white/60" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
